import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import FlowmaPowered from '../../Components/powered-by-flowma-component';
import SystemDataService from '../../Services/SystemDataService';
import NavLink from '../NavLink/navlink-component';
import { getServicesData } from '../../Public/services-data';
import { getFooterData } from '../../Public/footer-data';
import MediaComponent from '../Media/media-component';
import { toMediaUrl } from '../../Services/ApiService';
import { ASSETS } from '../../Public/assets-data';

/**
 * Footer, diagonal masked split, mirroring the landing hero.
 *   • BACK (Layer 0): a lifestyle photo fills the section.
 *   • Layer 1: a navy gradient darkens the left so the brand copy reads.
 *   • Layer 2: a white PNG MASK whose transparent window reveals the darkened
 *     photo on the left and covers the right in solid white for the columns.
 *   • Layer 10: content, the brand block (logo + headline) over the photo,
 *     and the Company / Services / Areas / Contact columns + CTA + legal bar
 *     over the white.
 *
 * Nothing is hardcoded: the logo comes from businessInfo.mainLogo, contact
 * details from businessInfo, and the mask/photo from S3 link records, so a new
 * mask is dropped in by uploading it and labelling it "Footer Mask" (no code
 * change). Mobile stacks the dark banner over the light content. Mirrors for RTL.
 */
function Footer() {
    const { dir = 'rtl', language = 'he', businessInfo = {}, links = [] } = useSelector((s) => s.systemData || {});
    const isRtl = dir !== 'ltr';

    const [services, setServices] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const sData = await getServicesData(language);
                setServices(Array.isArray(sData) ? sData.slice(0, 5) : []);
            } catch (err) {
                console.error('Footer async fetch error:', err);
            }
        }
        fetchData();
    }, [language]);

    const footerTxt = useMemo(() => getFooterData(language), [language]);
    const navLinks = footerTxt.navLinks || [];

    // ── Brand + contact straight from the per-client businessInfo (S3) ──
    const brandName = businessInfo?.businessName || '';
    const logoSrc = businessInfo?.mainLogo
        ? toMediaUrl(businessInfo.mainLogo)
        : (SystemDataService.byLabel(links, 'Logo White')?.image || SystemDataService.byLabel(links, 'Logo Full White')?.image || SystemDataService.byLabel(links, 'Logo')?.image || '');
    const phoneVal = businessInfo?.phone || '';
    const emailVal = businessInfo?.email || '';
    const addressVal = businessInfo?.address || businessInfo?.city || '';
    const telHref = phoneVal ? `tel:${phoneVal.replace(/[^\d+]/g, '')}` : '';
    const mailHref = emailVal ? `mailto:${emailVal}` : '';
    const mapsHref = addressVal ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressVal)}` : '';

    // ── Diagonal mask + background photo, data-driven, never hardcoded ──
    // Upload a new mask to the gallery and label the link "Footer Mask" to swap it.
    const maskLink = SystemDataService.byLabel(links, 'Footer Mask') || SystemDataService.byLabel(links, 'Footer Diagonal Mask');
    const maskSrc = maskLink?.image ? toMediaUrl(maskLink.image) : '';
    const photoLink = SystemDataService.byLabel(links, 'Footer Background') || SystemDataService.byLabel(links, 'Footer Image') || SystemDataService.byLabel(links, 'Hero Background');
    // Photo may fall back to a lifestyle asset so it still renders design-time.
    const photoSrc = (photoLink?.image ? toMediaUrl(photoLink.image) : '')
        || (ASSETS.WATERFRONT_MODERN_HOME_POOL_PALM_TREES_FLORIDA ? toMediaUrl(ASSETS.WATERFRONT_MODERN_HOME_POOL_PALM_TREES_FLORIDA) : '');

    // ── Platform social row (light bottom bar), from S3 links ──
    const socialDefs = [
        ['Instagram', 'instagram'], ['Facebook', 'facebook'], ['Linkedin', 'linkedin'],
        ['YouTube', 'youtube'], ['Twitter', 'x'], ['X', 'x'], ['WhatsApp', 'whatsapp'], ['TikTok', 'tiktok'],
    ];
    const seenSocial = new Set();
    const socialLinks = socialDefs.reduce((acc, [label, type]) => {
        if (seenSocial.has(type)) return acc;
        const link = SystemDataService.byLabel(links, label);
        if (link?.url) { seenSocial.add(type); acc.push({ url: link.url, type, label }); }
        return acc;
    }, []);

    // ── Contact-column rows (circular icon + bold label + value) ──
    const contactRows = [
        addressVal && { icon: 'pin', href: mapsHref, external: true, title: footerTxt.locationTitle, sub: footerTxt.locationNote },
        phoneVal && { icon: 'phone', href: telHref, title: footerTxt.callUsLabel, sub: phoneVal },
        emailVal && { icon: 'mail', href: mailHref, title: footerTxt.emailUsLabel, sub: emailVal, breakAll: true },
    ].filter(Boolean);

    const serviceItems = services.length
        ? services.map((s) => ({ label: s.name || s.title, href: `/services/${s.slug || s.id}` }))
        : navLinks;

    const callHref = telHref || '/contact';

    // Navy gradient over the photo, dark on the copy side, clearing toward the
    // white mask on the other. Mirrored for RTL.
    const darkOverlay = isRtl
        ? 'linear-gradient(to left, rgba(2,6,23,0.88) 0%, rgba(2,6,23,0.6) 30%, rgba(2,6,23,0) 55%)'
        : 'linear-gradient(to right, rgba(2,6,23,0.88) 0%, rgba(2,6,23,0.6) 30%, rgba(2,6,23,0) 55%)';
    // Fallback white wedge when no mask has been uploaded yet (keeps the split).
    const whiteClip = isRtl
        ? 'polygon(0 0, 60% 0, 70% 100%, 0 100%)'
        : 'polygon(40% 0, 100% 0, 100% 100%, 30% 100%)';
    const contentPad = isRtl ? 'lg:pr-[40%] lg:pl-10' : 'lg:pl-[40%] lg:pr-10';
    const brandWedge = isRtl ? 'lg:right-0 lg:pr-12' : 'lg:left-0 lg:pl-12';

    const brand = (
        <BrandPanel logoSrc={logoSrc} brandName={brandName} blurb={businessInfo?.description} txt={footerTxt} />
    );

    return (
        <footer dir={dir} className="relative w-full overflow-hidden bg-white text-primary-500">

            {/* ── Desktop masked diagonal (photo → dark overlay → white mask) ── */}
            <div className="pointer-events-none absolute inset-0 z-0 hidden lg:block" aria-hidden="true">
                {photoSrc && (
                    <MediaComponent src={photoSrc} alt="" width={1400} objectFit="cover" quality={55} useSekeleton={false} wrapperClassName="absolute inset-0 h-full w-full" />
                )}
                <div className="absolute inset-0" style={{ background: darkOverlay }} />
                {maskSrc ? (
                    <MediaComponent src={maskSrc} alt="" width={1400} quality={70} useSekeleton={false} wrapperClassName="absolute inset-0 h-full w-full" className="!object-fill scale-[1.02]" />
                ) : (
                    <div className="absolute inset-0 bg-white" style={{ clipPath: whiteClip }} />
                )}
            </div>

            {/* ── Desktop brand content, centered in the revealed photo wedge ── */}
            <div className={`hidden lg:flex lg:flex-col lg:justify-center lg:absolute lg:inset-y-0 lg:w-[34%] lg:z-10 lg:py-16 ${brandWedge}`}>
                {brand}
            </div>

            {/* ── Mobile dark brand banner (photo + overlay, angled bottom) ── */}
            <div className="relative z-0 w-full overflow-hidden px-6 pt-16 pb-20 lg:hidden" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 94%, 0 100%)' }}>
                {photoSrc && (
                    <div className="absolute inset-0">
                        <MediaComponent src={photoSrc} alt="" width={800} objectFit="cover" quality={50} useSekeleton={false} wrapperClassName="h-full w-full" />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-primary-950/60 via-primary-950/75 to-primary-950/90" />
                <div className="relative">{brand}</div>
            </div>

            {/* ── Light content panel (columns + card + bottom bar) ── */}
            <div className={`relative z-10 w-full px-6 py-14 sm:px-10 lg:py-16 ${contentPad}`}>

                <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-3">
                    <FooterCol title={footerTxt.companyHeading} items={footerTxt.companyLinks} />
                    <FooterCol title={footerTxt.ServicesHeading} items={serviceItems} />

                    {/* Areas We Serve, disabled for now.
                    <div>
                        <ColHeading>{footerTxt.areasHeading}</ColHeading>
                        <div className="flex flex-col gap-3">
                            {(footerTxt.areaLinks || []).map((item) => (
                                <NavLink key={item.label} href={item.href} className="text-[15px] text-primary-500 hover:text-secondary-600 transition-colors">
                                    {item.label}
                                </NavLink>
                            ))}
                            {footerTxt.areasAllLabel && (
                                <NavLink
                                    href={footerTxt.areasAllHref || '/contact'}
                                    preserveStyle
                                    showUnderline={false}
                                    className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-500 hover:text-secondary-600 transition-colors mt-1"
                                >
                                    {footerTxt.areasAllLabel}
                                    <Arrow isRtl={isRtl} />
                                </NavLink>
                            )}
                        </div>
                    </div>
                    */}

                    {/* Contact Us */}
                    {contactRows.length > 0 && (
                        <div>
                            <ColHeading>{footerTxt.contactHeading}</ColHeading>
                            <div className="flex flex-col gap-4">
                                {contactRows.map((row) => (
                                    <div key={row.title} className="flex items-center gap-3">
                                        <span className="shrink-0 w-10 h-10 rounded-full ring-1 ring-primary-200 text-primary-500 flex items-center justify-center">
                                            <ContactIcon type={row.icon} />
                                        </span>
                                        <div className="min-w-0 leading-tight">
                                            <span className="block text-primary-500 font-bold text-sm">{row.title}</span>
                                            {row.sub && (
                                                row.href ? (
                                                    <NavLink
                                                        href={row.href}
                                                        target={row.external ? '_blank' : undefined}
                                                        preserveStyle
                                                        showUnderline={false}
                                                        className={`block text-primary-300 text-sm hover:text-secondary-600 transition-colors ${row.breakAll ? 'break-all' : ''}`}
                                                    >
                                                        {row.sub}
                                                    </NavLink>
                                                ) : (
                                                    <span className={`block text-primary-300 text-sm ${row.breakAll ? 'break-all' : ''}`}>{row.sub}</span>
                                                )
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── "Need help now?" card ── */}
                <div className="mt-12 rounded-2xl border border-primary-100 bg-white px-6 py-6 md:px-8 flex flex-col items-center gap-6 text-center md:flex-row md:text-start">
                    <div className="flex-1">
                        <h3 className="font-secondary text-2xl font-bold text-primary-500 leading-tight">{footerTxt.helpHeading}</h3>
                        <p className="text-primary-300 text-sm mt-1">{footerTxt.helpSubtext}</p>
                    </div>
                    <div className="hidden md:block w-px self-stretch bg-primary-100" />
                    <NavLink
                        href={callHref}
                        preserveStyle
                        showUnderline={false}
                        className="inline-flex items-center gap-2.5 rounded-full bg-secondary-500 px-8 py-4 font-secondary font-bold text-primary-950 hover:bg-secondary-600 transition-colors active:scale-[0.98]"
                    >
                        {footerTxt.callNowLabel}
                        <Arrow isRtl={isRtl} strong />
                    </NavLink>
                </div>

                {/* ── Bottom bar ── */}
                <div className="mt-10 border-t border-primary-100 pt-6 flex flex-col items-center gap-4 md:flex-row md:justify-between">
                    <p className="text-sm text-primary-300 order-3 md:order-1">
                        © {new Date().getFullYear()} {brandName} {footerTxt.allRightsReserved}
                    </p>

                    <div className="flex gap-6 text-sm text-primary-400 order-1 md:order-2">
                        <NavLink href="/privacy" className="hover:text-secondary-600 transition-colors">{footerTxt.privacyText}</NavLink>
                        <NavLink href="/terms" className="hover:text-secondary-600 transition-colors">{footerTxt.termsText}</NavLink>
                    </div>

                    {socialLinks.length > 0 && (
                        <div className="flex items-center gap-4 order-2 md:order-3">
                            {socialLinks.map(({ url, type, label }) => (
                                <a
                                    key={type}
                                    href={url}
                                    target={/^https?:/i.test(url) ? '_blank' : undefined}
                                    rel="noopener noreferrer"
                                    aria-label={label}
                                    className="text-primary-500 hover:text-secondary-600 transition-colors"
                                >
                                    <SocialIcon type={type} />
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                <div className="pt-6 opacity-70 scale-90 origin-center md:origin-left">
                    <FlowmaPowered language={language} seo={false} />
                </div>
            </div>
        </footer>
    );
}

/* ── Dark brand panel (reused by desktop wedge + mobile banner) ── */
function BrandPanel({ logoSrc, brandName, blurb, txt }) {
    const blurbText = blurb && !/add your content here/i.test(blurb) ? blurb : txt.brandBlurb;
    return (
        <div className="max-w-xs mx-auto text-center lg:mx-0 lg:text-start">
            {logoSrc && (
                <MediaComponent
                    src={logoSrc}
                    alt={brandName ? `${brandName} logo` : 'logo'}
                    objectFit="contain"
                    wrapperClassName="h-16 w-auto max-w-[260px] mb-8 mx-auto lg:mx-0 [&_img]:object-center lg:[&_img]:object-left"
                    quality={100}
                    useSekeleton={false}
                />
            )}

            {brandName && (
                <h2 className="font-secondary text-4xl font-bold text-white leading-[1.05] xl:text-5xl">{brandName}</h2>
            )}
            <p className="mt-6 text-sm leading-relaxed text-white/60">{blurbText}</p>
        </div>
    );
}

function ColHeading({ children }) {
    return <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-primary-300 mb-5">{children}</h3>;
}

function FooterCol({ title, items = [] }) {
    const list = items.filter((i) => i && i.label).slice(0, 6);
    if (!list.length) return null;
    return (
        <div>
            <ColHeading>{title}</ColHeading>
            <div className="flex flex-col gap-3">
                {list.map((item) => (
                    <NavLink key={item.href || item.label} href={item.href} className="text-[15px] text-primary-500 hover:text-secondary-600 transition-colors">
                        {item.label}
                    </NavLink>
                ))}
            </div>
        </div>
    );
}

function Arrow({ isRtl, strong }) {
    return (
        <svg className={`w-4 h-4 ${strong ? 'w-5 h-5' : ''} ${isRtl ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strong ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
    );
}

// Line icons for the contact rows (navy stroke, on the light panel).
function ContactIcon({ type }) {
    const cls = 'w-5 h-5';
    const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
    if (type === 'phone') return (
        <svg className={cls} viewBox="0 0 24 24" {...common}>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
    );
    if (type === 'mail') return (
        <svg className={cls} viewBox="0 0 24 24" {...common}>
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 6-10 7L2 6" />
        </svg>
    );
    if (type === 'pin') return (
        <svg className={cls} viewBox="0 0 24 24" {...common}>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
        </svg>
    );
    return null;
}

function SocialIcon({ type }) {
    const cls = 'w-5 h-5 fill-current';
    if (type === 'instagram') return (
        <svg className={cls} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
    );
    if (type === 'facebook') return (
        <svg className={cls} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
    );
    if (type === 'linkedin') return (
        <svg className={cls} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
    );
    if (type === 'youtube') return (
        <svg className={cls} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
    );
    if (type === 'x') return (
        <svg className={cls} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
    if (type === 'whatsapp') return (
        <svg className={cls} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2.9C8.8 2.9 3 8.7 3 15.9c0 2.3.6 4.5 1.8 6.5L3 29.1l6.9-1.8c1.9 1 4 1.6 6.2 1.6 7.2 0 13-5.8 13-13S23.2 2.9 16 2.9zm0 23.7c-2 0-3.9-.5-5.6-1.5l-.4-.2-4.1 1.1 1.1-4-.3-.4C5.6 20 5 18 5 15.9 5 9.8 9.9 4.9 16 4.9s11 4.9 11 11-4.9 11-11 11zm6-8.2c-.3-.2-1.9-.9-2.2-1s-.5-.2-.7.2-.8 1-1 1.2-.4.2-.7.1c-.3-.2-1.4-.5-2.6-1.6-1-.9-1.6-2-1.8-2.3s0-.5.2-.7l.5-.5c.1-.2.2-.4.3-.6 0-.2 0-.4-.1-.6-.1-.2-.7-1.8-.9-2.4-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4s-1 1-1 2.4 1 2.8 1.1 3c.1.2 2 3.1 4.8 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.6-.7 1.8-1.3.2-.6.2-1.2.1-1.3-.1-.1-.3-.2-.6-.4z" />
        </svg>
    );
    if (type === 'tiktok') return (
        <svg className={cls} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.22 8.22 0 004.84 1.56V6.78a4.85 4.85 0 01-1.07-.09z" />
        </svg>
    );
    return null;
}

export default Footer;
